import { Box, Button, ButtonBase, CircularProgress, Divider, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
} from "react-hook-form";
import DoneAllIcon from "@mui/icons-material/DoneAll";

import { WSIcons } from "../../commonFunctions/iIcons";
import { useSnackbar } from "../Snackbar/SnackBarContext";
import {
  getNotificationPath,
  useVendorNotifications,
  VendorNotification,
} from "../Notifications/useVendorNotifications";

interface NotificationDrawerProps {
  control?: Control<any>;
  errors?: FieldErrors;
  register: UseFormRegister<any>;
  id: any;
  reset: UseFormReset<any>;
  noteId?: string;
  setValue: UseFormSetValue<any>;
  onCancel: () => void;
}

const COLORS = {
  primary: "#098963",
  dark: "#022219",
  muted: "#828584",
  border: "#CEEEE5",
  unreadBg: "#F3FBF8",
};


const formatTime = (value: string) => {
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

// Today / Yesterday / Earlier
const getDayGroup = (value: string) => {
  const date = new Date(value);
  if (isNaN(date.getTime())) return "Earlier";
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  if (date >= startOfToday) return "Today";
  if (date >= startOfYesterday) return "Yesterday";
  return "Earlier";
};

const NotificationItem: React.FC<{ item: VendorNotification; onRead: () => void }> = ({ item, onRead }) => (
  <ButtonBase
    type="button"
    onClick={onRead}
    disabled={item.isRead}
    aria-label={`${item.isRead ? "" : "Unread: "}${item.title}${item.body ? `. ${item.body}` : ""}`}
    sx={{
      width: "100%",
      display: "flex",
      alignItems: "flex-start",
      gap: 1.5,
      p: 1.5,
      borderRadius: "12px",
      textAlign: "left",
      bgcolor: item.isRead ? "transparent" : COLORS.unreadBg,
      "&.Mui-disabled": { cursor: "default" },
      "&:hover": { bgcolor: item.isRead ? "transparent" : "#E7F6F2" },
      "&.Mui-focusVisible": { outline: `2px solid ${COLORS.primary}` },
    }}
  >
    <Box
      sx={{
        width: 32,
        height: 32,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "8px",
        border: `1px solid ${item.isRead ? COLORS.border : COLORS.primary}`,
      }}
    >
      <WSIcons.BsBox2Fill style={{ color: item.isRead ? COLORS.border : COLORS.primary, fontSize: 16 }} />
    </Box>

    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Typography sx={{ color: COLORS.dark, fontSize: "0.9rem", fontWeight: item.isRead ? 400 : 600 }}>
        {item.title}
      </Typography>
      {item.body && (
        <Typography sx={{ color: item.isRead ? COLORS.muted : COLORS.dark, fontSize: "0.85rem", mt: 0.25 }}>
          {item.body}
        </Typography>
      )}
      <Typography sx={{ color: COLORS.muted, fontSize: "0.75rem", mt: 0.5 }}>{formatTime(item.createdAt)}</Typography>
    </Box>

    {/* Unread dot */}
    {!item.isRead && (
      <Box sx={{ width: 8, height: 8, mt: 0.75, borderRadius: "50%", bgcolor: COLORS.primary, flexShrink: 0 }} />
    )}
  </ButtonBase>
);

const NotificationDrawer: React.FC<NotificationDrawerProps> = ({ onCancel }) => {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const { notifications, unreadCount, loading, error, refresh, markRead, markAllRead } = useVendorNotifications();
  const [markingAll, setMarkingAll] = useState(false);

  // The drawer is an inbox of unread notifications: marking one (or all) as read removes it.
  // Read notifications stay on the Recent Notifications page.
  const visible = useMemo(() => notifications.filter((n) => !n.isRead), [notifications]);

  const groups = useMemo(() => {
    const order = ["Today", "Yesterday", "Earlier"];
    const map = new Map<string, VendorNotification[]>();
    visible.forEach((n) => {
      const key = getDayGroup(n.createdAt);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(n);
    });
    return order.filter((key) => map.has(key)).map((key) => ({ key, items: map.get(key)! }));
  }, [visible]);

  // Open the notification: mark it read, close the drawer and go to its order
  const handleOpen = async (item: VendorNotification) => {
    const path = getNotificationPath(item);
    if (path) {
      onCancel();
      navigate(path);
    }
    const ok = await markRead(item.id);
    if (!ok) showMessage("Couldn't mark the notification as read. Please try again.", "error");
  };

  const handleMarkAll = async () => {
    setMarkingAll(true);
    const failed = await markAllRead();
    setMarkingAll(false);
    if (failed) showMessage(`${failed} notification(s) couldn't be marked as read. Please try again.`, "error");
  };

  return (
    <Box sx={{ mt: 1 }}>
      {/* Unread count + mark all */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
        <Typography sx={{ color: COLORS.muted, fontSize: "0.875rem" }}>
          {unreadCount ? `${unreadCount} unread` : ""}
        </Typography>
        <Button
          type="button"
          size="small"
          startIcon={markingAll ? <CircularProgress size={14} color="inherit" /> : <DoneAllIcon />}
          onClick={handleMarkAll}
          disabled={unreadCount === 0 || markingAll}
          sx={{ color: COLORS.primary, textTransform: "none", fontWeight: 600 }}
        >
          Mark all as read
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} sx={{ color: COLORS.primary }} />
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography sx={{ color: "#D32F2F" }}>Failed to load notifications.</Typography>
          <Button type="button" size="small" onClick={() => refresh(true)} sx={{ mt: 1, color: COLORS.primary, textTransform: "none" }}>
            Try again
          </Button>
        </Box>
      ) : visible.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography sx={{ color: COLORS.dark, fontWeight: 600 }}>You're all caught up</Typography>
          <Typography sx={{ color: COLORS.muted, fontSize: "0.875rem", mt: 0.5 }}>
            No unread notifications. New order updates will appear here.
          </Typography>
        </Box>
      ) : (
        groups.map((group) => (
          <Box key={group.key} sx={{ mt: 2.5 }}>
            <Typography sx={{ color: COLORS.muted, fontSize: "0.8rem", fontWeight: 600, mb: 1 }}>{group.key}</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {group.items.map((item, index) => (
                <React.Fragment key={item.id || index}>
                  <NotificationItem item={item} onRead={() => handleOpen(item)} />
                  {index < group.items.length - 1 && <Divider sx={{ borderColor: "#F0F0F0" }} />}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
};

export default NotificationDrawer;
